// src/pages/AdminProductForm.jsx
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { getSizeLabel } from '../utils/constants';

const AdminProductForm = ({ initialData, onSuccess }) => {
  const isEditMode = !!initialData;

  // Core catalog details matching your Spring Boot ProductRequest DTO
  const [productMetadata, setProductMetadata] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category: initialData?.category || 'T_SHIRT',
    
    // Checks both flat initialData.collegeId AND nested initialData.college.id responses
    collegeId: initialData?.collegeId !== undefined 
      ? String(initialData.collegeId) 
      : initialData?.college?.id !== undefined 
        ? String(initialData.college.id) 
        : '',
        
    discount: initialData?.discount !== null && initialData?.discount !== undefined ? initialData.discount : '',
    featured: initialData?.featured || false
  });

  // Structural dynamic tracking states
  const [colleges, setColleges] = useState([]);
  
  // Initialize size variants pricing matrix array
  const [sizes, setSizes] = useState(
    initialData?.availableSizes || [{ size: 'S', stockQuantity: 10, price: 999 }]
  );

  // MATCHED INITIALIZER: Targets backend's exact fields (imageUrls & imageType)
  const [images, setImages] = useState(() => {
    if (initialData && initialData.imageUrls && initialData.imageUrls.length > 0) {
      const typeSource = initialData.imageType || initialData.imageTypes || [];
      return initialData.imageUrls.map((url, index) => ({
        imageUrl: url,
        imageType: typeSource[index] || 'Front'
      }));
    }
    return [{ imageUrl: '', imageType: 'Front' }];
  });

  const [status, setStatus] = useState({ type: '', message: '' });

  // Domain Config Options matching backend Enums
  const CATEGORIES = ['T_SHIRT', 'HOODIE'];
  // Enum values sent to the backend; SIZE_LABELS maps them to the customer-facing text.
  const VALID_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];
  const IMAGE_TYPES = ['Front', 'Back', 'Side', 'Male_Model', 'Female_Model'];

  // Fetch active college index matrices on mount
  useEffect(() => {
    api.get('/colleges') 
      .then(res => {
        if (res.data && res.data.length > 0) setColleges(res.data);
      })
      .catch(err => console.error("Error connecting to catalog college indices:", err));
  }, []);

  // 🟢 FIXED SIZE CHANGE MUTATOR: Permits temporary empty strings so backspacing works natively
  const handleSizeChange = (idx, field, val) => {
    const updated = [...sizes];
    if (field === 'size') {
      updated[idx][field] = val;
    } else {
      updated[idx][field] = val === '' ? '' : Number(val);
    }
    setSizes(updated);
  };

  // Parallel Image Track Array Mutators
  const handleImageChange = (idx, field, val) => {
    const updated = [...images];
    updated[idx][field] = val;
    setImages(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    // Scrub completely empty media fields out to satisfy optional input targets
    const validMediaAssets = images.filter(img => img.imageUrl && img.imageUrl.trim() !== '');

    // Packaging form values into parallel primitive layout structures required by API DTO
    const requestPayload = {
      ...productMetadata,
      collegeId: Number(productMetadata.collegeId),
      discount: productMetadata.discount !== '' ? Number(productMetadata.discount) : null, // Handle null gracefully
      availableSizes: sizes.map(({ id, finalPrice, ...rest }) => ({
        ...rest,
        // 🟢 SANITIZATION GUARD: Fallback any temporary blank typing string fields back to 0 for DTO safety
        stockQuantity: rest.stockQuantity === '' ? 0 : Number(rest.stockQuantity),
        price: rest.price === '' ? 0 : Number(rest.price)
      })),
      imageUrls: validMediaAssets.map(img => img.imageUrl),
      imageType: validMediaAssets.map(img => img.imageType) 
    };

    try {
      if (isEditMode) {
        // FIXED ENDPOINT PATH: Stripped duplicate `/api` context prefixing
        await api.put(`/admin/products/${initialData.id}`, requestPayload);
        alert('VidyaThreads store inventory entry updated successfully!');
      } else {
        await api.post('/admin/products', requestPayload);
        alert('VidyaThreads store item published successfully!');
        
        // Reset local hooks only on explicit creations
        setProductMetadata({ name: '', description: '', category: 'T_SHIRT', collegeId: '', discount: '', featured: false });
        setSizes([{ size: 'S', stockQuantity: 10, price: 999 }]);
        setImages([{ imageUrl: '', imageType: 'Front' }]);
      }

      if (onSuccess) onSuccess();

    } catch (err) {
      setStatus({ 
        type: 'error', 
        message: err.response?.data?.message || 'Database pipeline connectivity fault.' 
      });
    }
  };

  return (
    <div style={styles.formContainer}>
      <style>{`
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>

      <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
        {isEditMode ? `Modify Inventory Entry — ID: ${initialData.id}` : 'Upload New Campus Merchandise'}
      </h3>
      
      {status.message && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '6px',
          backgroundColor: status.type === 'success' ? '#e6fffa' : '#fff5f5',
          color: status.type === 'success' ? '#234e52' : '#742a2a',
          border: `1px solid ${status.type === 'success' ? '#b2f5ea' : '#fed7d7'}`
        }}>
          {status.message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Core Info Row */}
        <div style={styles.formGrid}>
          <div>
            <label style={styles.label}>Product Title</label>
            <input type="text" value={productMetadata.name} onChange={e => setProductMetadata({...productMetadata, name: e.target.value})} style={styles.input} placeholder="e.g., IIT Bombay Core Hoodie" required />
          </div>
          <div>
            <label style={styles.label}>Target Institution</label>
            <select 
              value={productMetadata.collegeId} 
              onChange={e => setProductMetadata({...productMetadata, collegeId: e.target.value})} 
              style={{ ...styles.input, backgroundColor: '#fff', color: '#000' }} 
              required
            >
              <option value="">-- Choose College Context --</option>
              {colleges.map(c => <option key={c.id} value={String(c.id)}>{c.name} ({c.code})</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={styles.label}>Product Catalog Description</label>
          <textarea value={productMetadata.description} onChange={e => setProductMetadata({...productMetadata, description: e.target.value})} style={{...styles.input, height: '70px', resize: 'vertical'}} placeholder="Premium quality heavy cotton blend drop shoulder..." required />
        </div>

        <div style={{ ...styles.formGrid, gridTemplateColumns: '1fr 1fr 1fr', marginBottom: '25px' }}>
          <div>
            <label style={styles.label}>Category</label>
            <select value={productMetadata.category} onChange={e => setProductMetadata({...productMetadata, category: e.target.value})} style={styles.input}>
              {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div>
            <label style={styles.label}>Discount (%)</label>
            <input type="number" min="0" max="100" value={productMetadata.discount} onChange={e => setProductMetadata({...productMetadata, discount: e.target.value === '' ? '' : Number(e.target.value)})} style={styles.input} placeholder="0" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '25px' }}>
            <label style={{ cursor: 'pointer', fontWeight: '500' }}>
              <input type="checkbox" checked={productMetadata.featured} onChange={e => setProductMetadata({...productMetadata, featured: e.target.checked})} style={{ marginRight: '8px' }} /> 
              Promote to Showcase
            </label>
          </div>
        </div>

        {/* Variant Matrix Section */}
        <h4 style={styles.sectionHeading}>Variant Pricing & Stock Matrix</h4>
        <div style={styles.columnHeaderRow}>
          <span style={{ ...styles.columnLabel, flex: 1 }}>Size</span>
          <span style={{ ...styles.columnLabel, flex: 1 }}>Stock Quantity</span>
          <span style={{ ...styles.columnLabel, flex: 1 }}>Base Price (INR)</span>
          {sizes.length > 1 && <span style={{ width: '68px' }}></span>}
        </div>

        {sizes.map((sz, idx) => (
          <div key={idx} style={styles.dynamicRow}>
            <select value={sz.size} onChange={e => handleSizeChange(idx, 'size', e.target.value)} style={{ ...styles.input, flex: 1 }}>
              {VALID_SIZES.map(v => <option key={v} value={v}>{getSizeLabel(v)}</option>)}
            </select>
            <input type="number" placeholder="Stock Qty" value={sz.stockQuantity} onChange={e => handleSizeChange(idx, 'stockQuantity', e.target.value)} style={{ ...styles.input, flex: 1 }} required />
            <input type="number" placeholder="Base Price" value={sz.price} onChange={e => handleSizeChange(idx, 'price', e.target.value)} style={{ ...styles.input, flex: 1 }} required />
            {sizes.length > 1 && (
              <button type="button" onClick={() => setSizes(sizes.filter((_, i) => i !== idx))} style={styles.deleteBtn}>Delete</button>
            )}
          </div>
        ))}
        <button type="button" onClick={() => setSizes([...sizes, { size: 'M', stockQuantity: 10, price: 999 }])} style={styles.addNestedBtn}>+ Add Size Step</button>

        {/* Media Gallery Section */}
        <h4 style={styles.sectionHeading}>Product Display Assets (Media Gallery) — Optional</h4>
        <div style={styles.columnHeaderRow}>
          <span style={{ ...styles.columnLabel, flex: 3 }}>Image Asset URL Path</span>
          <span style={{ ...styles.columnLabel, flex: 1.5 }}>Display Angle / Type</span>
          {images.length > 1 && <span style={{ width: '68px' }}></span>}
        </div>

        {images.map((img, idx) => (
          <div key={idx} style={{ marginBottom: '16px', borderBottom: '1px dashed #edf2f7', paddingBottom: '16px' }}>
            <div style={styles.dynamicRow}>
              <input 
                type="text" 
                placeholder="https://example-cdn.com/images/hoodie-front.jpg" 
                value={img.imageUrl} 
                onChange={e => handleImageChange(idx, 'imageUrl', e.target.value)} 
                style={{ ...styles.input, flex: 3 }} 
              />
              <select 
                value={img.imageType} 
                onChange={e => handleImageChange(idx, 'imageType', e.target.value)} 
                style={{ ...styles.input, flex: 1.5 }}
              >
                {IMAGE_TYPES.map(type => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}
              </select>

              {images.length > 1 && (
                <button type="button" onClick={() => setImages(images.filter((_, i) => i !== idx))} style={styles.deleteBtn}>Delete</button>
              )}
            </div>

            {img.imageUrl && img.imageUrl.trim().startsWith('http') && (
              <div style={styles.previewContainer}>
                <img src={img.imageUrl} alt="Preview Asset" style={styles.thumbnailPreview} onError={(e) => { e.target.style.display = 'none'; }} />
                <span style={styles.previewTag}>Live View: {img.imageType.replace('_', ' ')} Angle</span>
              </div>
            )}
          </div>
        ))}
        
        <button type="button" onClick={() => setImages([...images, { imageUrl: '', imageType: 'Front' }])} style={styles.addNestedBtn}>+ Add New Image URL Row</button>

        <button type="submit" style={{ ...styles.submitBtn, backgroundColor: isEditMode ? '#ecc94b' : '#48bb78', color: isEditMode ? '#744210' : '#ffffff' }}>
          {isEditMode ? 'Commit Variations to Production Registry' : 'Commit Merchandise to Production'}
        </button>
      </form>
    </div>
  );
};

const styles = {
  formContainer: { background: '#ffffff', border: '1px solid #e2e8f0', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' },
  label: { display: 'block', fontWeight: '600', fontSize: '0.9rem', marginBottom: '6px', color: '#4a5568' },
  input: { width: '100%', padding: '10px', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' },
  sectionHeading: { borderBottom: '1px solid #edf2f7', paddingBottom: '8px', marginTop: '30px', marginBottom: '15px', color: '#2d3748', fontWeight: 'bold' },
  columnHeaderRow: { display: 'flex', gap: '12px', marginBottom: '8px', paddingRight: '2px' },
  columnLabel: { fontWeight: '600', fontSize: '0.85rem', color: '#718096' },
  dynamicRow: { display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' },
  previewContainer: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', padding: '6px 12px', background: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0', width: 'fit-content' },
  thumbnailPreview: { width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #cbd5e0', background: '#fff' },
  previewTag: { fontSize: '0.8rem', color: '#4a5568', fontWeight: '500' },
  addNestedBtn: { background: 'none', border: 'none', color: '#2b6cb0', fontWeight: 'bold', cursor: 'pointer', padding: 0, fontSize: '0.9rem', marginBottom: '20px', display: 'block' },
  deleteBtn: { backgroundColor: '#fed7d7', color: '#9b2c2c', border: 'none', padding: '10px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', width: '68px', boxSizing: 'border-box', textAlign: 'center' },
  submitBtn: { width: '100%', padding: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1.05rem', fontWeight: 'bold', marginTop: '10px', transition: 'background 0.2s' }
};

export default AdminProductForm;